import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Link,
  Stack,
  CircularProgress,
  Popover,
  Tooltip,
  TablePagination,
  Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { InsightSearchField } from "../components/RegulationsTable/FilterBar/InsightSearchField";
import NoDataImage from "../assets/no-data-anim.webp";
import { 
  globalSearch, 
  GlobalSearchRecord,
  Market,
  ProductType,
  CategoryItem,
  visibleProductTypes,
} from "../services/regulationsService";
import {
  buildFusionShellUrl,
  buildRegulationDetailPath,
  resolveGlobalSearchSourceDomain,
} from "../utils/externalNavigation";
import { RegulationFilterPanel } from "../components/RegulationsTable/FilterBar/RegulationFilterPanel";
import {
  emptyFilterSelection,
  filterSelectionToIncludes,
  type FilterSelectionState,
} from "../components/RegulationsTable/FilterBar/regulationFilterDraft";

/**
 * Format date string to DD-MMM-YYYY
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate().toString().padStart(2, "0");
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day}-${month}-${year}`;
};

/**
 * Format market data to string
 */
const formatMarkets = (markets: Market[]): string => {
  if (!markets || markets.length === 0) return "-";
  const names: string[] = [];
  markets.forEach(m => {
    if (m.children?.length) {
      m.children.forEach(c => names.push(c.name));
    } else {
      names.push(m.name);
    }
  });
  return names.join(", ");
};

/**
 * Format product types to string
 */
const formatProductTypes = (pts: ProductType[]): string => {
  const visible = visibleProductTypes(pts);
  if (visible.length === 0) return "-";
  return visible.map((p) => p.name).join(", ");
};

/**
 * Format categories to string
 */
const formatCategories = (cats: CategoryItem[]): string => {
  if (!cats || cats.length === 0) return "-";
  const names: string[] = [];
  const collect = (items: CategoryItem[]) => {
    items.forEach(item => {
      names.push(item.name);
      if (item.children?.length) collect(item.children);
    });
  };
  collect(cats);
  return names.join(", ");
};

/** Highlight keys from API (try first match with non-empty array). */
const GLOBAL_SEARCH_HIGHLIGHT_KEYS = {
  domain: ["domain_id.name", "domain.name", "domain_name"],
  productTypes: [
    "productType.children.name",
    "productType.name",
    "product_types.name",
    "productType",
    "product_types",
  ],
  markets: ["markets.children.name", "markets.name", "markets"],
  categories: [
    "categories.children.children.name",
    "categories.name",
    "categories",
  ],
} as const;

function pickGlobalSearchHighlight(
  highlight: GlobalSearchRecord["highlight"] | undefined,
  candidateKeys: readonly string[],
): string[] | undefined {
  if (!highlight) return undefined;
  const h = highlight as Record<string, string[] | undefined>;
  for (const key of candidateKeys) {
    const arr = h[key];
    if (Array.isArray(arr) && arr.length > 0) return arr;
  }
  return undefined;
}

/** Subpixel tolerance for overflow checks */
const OVERFLOW_EPS = 1;

/**
 * True when element content exceeds visible area (ellipsis / line-clamp).
 */
function useContentOverflow<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  mode: "horizontal" | "vertical" | "both",
  deps: React.DependencyList,
): boolean {
  const [overflow, setOverflow] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      setOverflow(false);
      return;
    }
    const measure = () => {
      const h = el.scrollWidth > el.clientWidth + OVERFLOW_EPS;
      const v = el.scrollHeight > el.clientHeight + OVERFLOW_EPS;
      if (mode === "horizontal") setOverflow(h);
      else if (mode === "vertical") setOverflow(v);
      else setOverflow(h || v);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode, ref, ...deps]);
  return overflow;
}

/** Five equal columns; must match bottom tag row for alignment */
const SEARCH_RESULT_GRID_ROW_SX = {
  display: "flex",
  flexDirection: "row" as const,
  gap: 2,
  pr: "5%",
  "& > *": { flex: 1, minWidth: 0 },
};

interface SearchResultItemProps {
  record: GlobalSearchRecord;
  query?: string;
}

const HighlightText = ({ text, highlights }: { text: string; highlights?: string[] }) => {
  if (!highlights || highlights.length === 0) return <>{text}</>;
  
  // Join multiple highlight fragments with ellipsis
  const combinedHtml = highlights.join(' ... ');
  return <span dangerouslySetInnerHTML={{ __html: combinedHtml }} />;
};

const HIGHLIGHT_MARK_SX = {
  backgroundColor: "#fff59d",
  color: "inherit",
  padding: "0 2px",
} as const;

/** Dark tooltip uses white body text; marks must use dark text on yellow (not inherit). */
const TOOLTIP_HIGHLIGHT_MARK_SX = {
  backgroundColor: "#fff59d",
  color: "#262626",
  padding: "0 2px",
} as const;

const TruncatedInfo = ({
  label,
  value,
  highlights,
  maxWidth = "150px",
}: {
  label: string;
  value: string;
  /** When API returns HTML highlight fragments, cell and tooltip both render this HTML. */
  highlights?: string[];
  maxWidth?: string;
}) => {
  const textRef = useRef<HTMLElement>(null);
  const hasHighlight = Boolean(highlights && highlights.length > 0);
  const combinedHtml = hasHighlight ? highlights!.join(" ... ") : "";
  const isOverflow = useContentOverflow(textRef, "horizontal", [
    value,
    combinedHtml,
    maxWidth,
    hasHighlight,
  ]);
  const showTooltip = Boolean(
    isOverflow && (hasHighlight || (Boolean(value) && value !== "-")),
  );

  const textSx = {
    display: "block",
    color: "#262626",
    fontWeight: 600,
    fontSize: "13px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: maxWidth,
    minWidth: 0,
    "& mark": HIGHLIGHT_MARK_SX,
  } as const;

  const tooltipTitle: React.ReactNode = hasHighlight ? (
    <Box
      sx={{
        maxWidth: 560,
        maxHeight: 260,
        overflow: "auto",
        fontSize: "13px",
        lineHeight: 1.6,
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontWeight: 600,
        color: "#fff",
        "& mark": TOOLTIP_HIGHLIGHT_MARK_SX,
      }}
    >
      <span dangerouslySetInnerHTML={{ __html: combinedHtml }} />
    </Box>
  ) : (
    value
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexShrink: 1 }}>
      <Typography variant="caption" sx={{ color: "#bfbfbf", fontSize: "13px", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Tooltip
        title={tooltipTitle}
        arrow
        disableHoverListener={!showTooltip}
        {...(hasHighlight
          ? {
              componentsProps: {
                tooltip: {
                  sx: {
                    bgcolor: "rgba(50, 50, 50, 0.95)",
                    maxWidth: 600,
                    "& .MuiTooltip-arrow": { color: "rgba(50, 50, 50, 0.95)" },
                  },
                },
              },
            }
          : {})}
      >
        <Typography
          ref={textRef}
          variant="caption"
          component="span"
          sx={textSx}
          {...(hasHighlight
            ? { dangerouslySetInnerHTML: { __html: combinedHtml } }
            : { children: value })}
        />
      </Tooltip>
    </Box>
  );
};

const SearchResultItem = ({ record, query }: SearchResultItemProps) => {
  const { source, highlight } = record;
  const synopsisRef = useRef<HTMLElement>(null);
  const synopsisOverflow = useContentOverflow(synopsisRef, "both", [
    source.synopsis,
    highlight?.synopsis,
  ]);
  const showSynopsisTooltip = Boolean(source.synopsis && synopsisOverflow);

  const synopsisHighlightHtml =
    highlight?.synopsis && highlight.synopsis.length > 0
      ? highlight.synopsis.join(" ... ")
      : "";
  const synopsisTooltipTitle: React.ReactNode =
    synopsisHighlightHtml.length > 0 ? (
      <Box
        sx={{
          color: "#fff",
          fontSize: "13px",
          lineHeight: 1.6,
          wordBreak: "break-word",
          whiteSpace: "normal",
          "& mark": TOOLTIP_HIGHLIGHT_MARK_SX,
        }}
      >
        <span dangerouslySetInnerHTML={{ __html: synopsisHighlightHtml }} />
      </Box>
    ) : (
      source.synopsis || ""
    );

  const [linksAnchorEl, setLinksAnchorEl] = useState<HTMLElement | null>(null);

  const handleLinksClick = (event: React.MouseEvent<HTMLElement>) => {
    setLinksAnchorEl(event.currentTarget);
  };

  const handleLinksClose = () => {
    setLinksAnchorEl(null);
  };

  const linksOpen = Boolean(linksAnchorEl);

  const constructModuleUrl = (record: GlobalSearchRecord) => {
    const { source } = record;
    const versionStr = source.version?.startsWith("v")
      ? source.version
      : `v${source.version}`;
    const domain = resolveGlobalSearchSourceDomain(
      source as Record<string, unknown>,
    );
    const modulePath = buildRegulationDetailPath(source.insight_id, {
      version: versionStr,
      domain,
    });
    return buildFusionShellUrl(modulePath);
  };

  return (
    <Box 
      sx={{ mb: 3, position: "relative" }}
    >
      {/* Title */}
      <Typography
        variant="h6"
        sx={{
          fontSize: "17px",
          fontWeight: 600,
          color: "#262626", 
          cursor: "pointer",
          mb: 0.8,
        }}
      >
        <HighlightText text={source.title} highlights={highlight?.title} />
      </Typography>

      {/* Meta info row: five equal columns, aligned with bottom tag row */}
      <Stack
        direction="row"
        sx={{
          ...SEARCH_RESULT_GRID_ROW_SX,
          mb: 1.5,
          alignItems: "center",
          fontSize: "13px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "#8c8c8c", fontSize: "13px" }}>ID</Typography>
          <Link 
            href={constructModuleUrl(record)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#295ef1", textDecoration: "underline", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}
          >
            {source.insight_id}
          </Link>
        </Box>

        <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 0.8, 
            cursor: "pointer",
            minWidth: 0,
            flexWrap: "wrap",
            "&:hover": { opacity: 0.8 }
          }}
          onClick={handleLinksClick}
        >
          <Typography variant="caption" sx={{ color: "#8c8c8c", fontSize: "13px" }}>Reference Links</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#262626", fontSize: "13px" }}>
            {source.referenceLinks?.length || 0}
          </Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#8c8c8c", ml: -0.3 }} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "#8c8c8c", fontSize: "13px" }}>Associated Documents</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#262626", fontSize: "13px" }}>
            {source.associatedDocuments?.length || 0}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "#8c8c8c", fontSize: "13px" }}>Release Date</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#262626", fontSize: "13px" }}>
            {formatDate(source.releaseDate)}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0 }} aria-hidden />
      </Stack>

      <Popover
        open={linksOpen}
        anchorEl={linksAnchorEl}
        onClose={handleLinksClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1.5,
            maxWidth: 500,
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            border: "1px solid #f0f0f0",
            borderRadius: "4px",
          }
        }}
      >
        <Stack spacing={1}>
          {source.referenceLinks && source.referenceLinks.length > 0 ? (
            source.referenceLinks.map((link, idx) => {
              const rawUrl = typeof link === "string" ? link : link.name;
              const url = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
              return (
                <Link 
                  key={idx} 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    fontSize: "13px", 
                    color: "#295ef1", 
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                    wordBreak: "break-all"
                  }}
                >
                  {rawUrl}
                </Link>
              );
            })
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#8c8c8c" }}>No links available</Typography>
          )}
        </Stack>
      </Popover>

      {/* Synopsis */}
      <Box sx={{ mb: 1.8, display: "flex", gap: 1.5 }}>
        <Typography variant="caption" sx={{ color: "#bfbfbf", fontSize: "13px", minWidth: "fit-content" }}>Synopsis</Typography>
        <Tooltip
          title={synopsisTooltipTitle}
          arrow
          disableHoverListener={!showSynopsisTooltip}
          enterDelay={300}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 600,
                maxHeight: 260,
                overflow: "auto",
                fontSize: "13px",
                lineHeight: 1.6,
                backgroundColor: "rgba(50, 50, 50, 0.95)",
                color: "#fff",
                padding: "10px 14px",
                "& mark": TOOLTIP_HIGHLIGHT_MARK_SX,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "2px",
                },
              },
            },
          }}
        >
          <Typography
            ref={synopsisRef}
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              color: "#595959",
              fontSize: "13px",
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
              cursor: showSynopsisTooltip ? "pointer" : "default",
              "& mark": {
                backgroundColor: "#fff59d",
                color: "inherit",
                padding: "0 2px",
              }
            }}
          >
            <HighlightText text={source.synopsis || ""} highlights={highlight?.synopsis} />
          </Typography>
        </Tooltip>
      </Box>

      {/* Bottom info tags */}
      <Stack 
        direction="row" 
        sx={{ 
          ...SEARCH_RESULT_GRID_ROW_SX,
          fontSize: "13px", 
          alignItems: "flex-start",
        }}
      >
        <TruncatedInfo
          label="Domain"
          value={source.domain_name || "MPR"}
          highlights={pickGlobalSearchHighlight(
            highlight,
            GLOBAL_SEARCH_HIGHLIGHT_KEYS.domain,
          )}
        />
        <TruncatedInfo
          label="Product Types"
          value={formatProductTypes(source.productType || [])}
          highlights={pickGlobalSearchHighlight(
            highlight,
            GLOBAL_SEARCH_HIGHLIGHT_KEYS.productTypes,
          )}
        />
        <TruncatedInfo
          label="Markets"
          value={formatMarkets(source.markets || [])}
          highlights={pickGlobalSearchHighlight(
            highlight,
            GLOBAL_SEARCH_HIGHLIGHT_KEYS.markets,
          )}
        />
        <TruncatedInfo
          label="Categories"
          value={formatCategories(source.categories || [])}
          highlights={pickGlobalSearchHighlight(
            highlight,
            GLOBAL_SEARCH_HIGHLIGHT_KEYS.categories,
          )}
        />
        <TruncatedInfo label="Brand Name" value="-" />
      </Stack>
      <Divider sx={{ mt: 3, borderColor: "#f0f0f0" }} />
    </Box>
  );
};

const GlobalSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryParam = searchParams.get("query") || searchParams.get("q") || "";
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const pageSizeParam = parseInt(searchParams.get("pagesize") || "10", 10);
  const semanticSearchParam = searchParams.get("semantic_search") === "true";
  
  const includesParam = searchParams.get("includes") 
    ? searchParams.get("includes")?.split(",").filter(i => i.trim() !== "") 
    : [];
  const excludesParam = searchParams.get("excludes")
    ? searchParams.get("excludes")?.split(",").filter(i => i.trim() !== "")
    : [];

  const [draftQuery, setDraftQuery] = useState(queryParam);
  const [appliedSelection, setAppliedSelection] = useState<FilterSelectionState>(
    () => emptyFilterSelection(),
  );

  useEffect(() => {
    setDraftQuery(queryParam);
  }, [queryParam]);

  const submitQuery = () => {
    const next = new URLSearchParams(searchParams);
    const trimmed = draftQuery.trim();
    if (trimmed) next.set("query", trimmed);
    else next.delete("query");
    next.set("page", "1");
    setSearchParams(next);
  };

  const handleApplyFilters = (nextSelection: FilterSelectionState) => {
    setAppliedSelection(nextSelection);
    const includes = filterSelectionToIncludes(nextSelection);
    const next = new URLSearchParams(searchParams);
    if (includes.length > 0) next.set("includes", includes.join(","));
    else next.delete("includes");
    next.set("page", "1");
    setSearchParams(next);
  };

  const handleClearFilters = () => {
    setAppliedSelection(emptyFilterSelection());
    const next = new URLSearchParams(searchParams);
    next.delete("includes");
    next.set("page", "1");
    setSearchParams(next);
  };
  
  const [results, setResults] = useState<GlobalSearchRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  // 监听来自父页面的 postMessage 消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 匹配截图中的 globalSearchChange 类型
      if (event.data && event.data.type === "globalSearchChange") {
        console.log("收到外部搜索消息:", event.data);
        
        const searchInfo = event.data.data;
        if (searchInfo && typeof searchInfo === "object") {
          const newParams = new URLSearchParams(searchParams);
          
          if (searchInfo.query !== undefined) {
            newParams.set("query", searchInfo.query);
          }
          
          if (searchInfo.semantic_search !== undefined) {
            newParams.set("semantic_search", searchInfo.semantic_search.toString());
          }
          
          newParams.set("page", "1");
          setSearchParams(newParams);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [searchParams, setSearchParams]);

  const fetchResults = async () => {
    // Avoid POST /global-search with empty query: iframe often loads without ?query=
    // before parent sends globalSearchChange via postMessage, which would error on backend.
    const trimmedQuery = queryParam.trim();
    if (!trimmedQuery) {
      setResults([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await globalSearch({
        query: trimmedQuery,
        page: pageParam,
        pagesize: pageSizeParam,
        semantic_search: semanticSearchParam,
        includes: includesParam,
        excludes: excludesParam,
      });
      if (response.status === "success") {
        setResults(response.data.records || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error("Global search failed:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, [
    queryParam,
    pageParam,
    pageSizeParam,
    semanticSearchParam,
    searchParams.get("includes"),
    searchParams.get("excludes"),
  ]);

  const handlePageChange = (event: unknown, newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", (newPage + 1).toString());
    setSearchParams(newParams);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    if (!Number.isFinite(newPageSize) || newPageSize <= 0) return;

    const safePage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
    const safeOldSize =
      Number.isFinite(pageSizeParam) && pageSizeParam > 0 ? pageSizeParam : 10;

    // Keep the same first visible record index: (page-1)*oldSize -> new page
    const firstIndex0 = (safePage - 1) * safeOldSize;
    let newPage = Math.floor(firstIndex0 / newPageSize) + 1;

    const maxPage =
      total > 0 ? Math.max(1, Math.ceil(total / newPageSize)) : 1;
    newPage = Math.min(maxPage, Math.max(1, newPage));

    const newParams = new URLSearchParams(searchParams);
    newParams.set("pagesize", String(newPageSize));
    newParams.set("page", String(newPage));
    setSearchParams(newParams);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        pt: "15px",
        pb: "34px",
        boxSizing: "border-box",
      }}
    >
      <Container maxWidth={false} sx={{ px: "20px !important", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <InsightSearchField
            value={draftQuery}
            onChange={setDraftQuery}
            onKeyDown={(event) => {
              if (event.key === "Enter") submitQuery();
            }}
            onClear={() => {
              setDraftQuery("");
              const next = new URLSearchParams(searchParams);
              next.delete("query");
              next.set("page", "1");
              setSearchParams(next);
            }}
          />
          <RegulationFilterPanel
            applied={appliedSelection}
            onApply={handleApplyFilters}
            onClearApplied={handleClearFilters}
          />
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress sx={{ color: "#295ef1" }} />
          </Box>
        ) : (
          <>
            <Stack spacing={0}>
              {(results || []).map((item, index) => (
                <SearchResultItem 
                  key={item.source?.id || item.source?.insight_id || index} 
                  record={item} 
                  query={queryParam} 
                />
              ))}
              {(!results || results.length === 0) && !loading && (
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: "column",
                    alignItems: "center", 
                    justifyContent: "center",
                    mt: 15
                  }}
                >
                  <Box 
                    component="img" 
                    src={NoDataImage} 
                    alt="No results found" 
                    sx={{ width: 160, height: "auto", mb: 2 }}
                  />
                  <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
                  No search results found.
                  </Typography>
                </Box>
              )}
            </Stack>
            
            {total > 0 && (
              <Box sx={{ mt: 0, display: "flex", justifyContent: "flex-end" }}>
                <TablePagination
                  component="div"
                  count={total}
                  page={pageParam - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={pageSizeParam}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[10, 20, 50]}
                  sx={{
                    borderTop: "none",
                    "& .MuiTablePagination-toolbar": {
                      fontSize: "13px",
                      color: "#595959",
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "13px",
                      color: "#595959",
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default GlobalSearchPage;
